import React, { useMemo } from 'react';
import { ProjectDraw } from '../project/project.type';
import { Dialog, DialogActions, DialogContent, DialogTitle, List, ListItemText, Checkbox, ListItemButton } from '@mui/material';
import { Button, Typography } from 'components/index';
import { useQueryClient } from 'react-query';
import { usePrompt } from '../AppPrompt';
import { toast } from 'react-toastify';
import { handleError } from 'common/utils/util.errors';

interface ProjectWithDraws {
  projectName: string;
  draws: ProjectDraw[];
}
interface ProjectsWithDraws {
  [projectId: string]: ProjectWithDraws;
}
interface ProjectDrawSelectionDropdownProps {
  updateDivisionDraws: (divisionId: string, newDrawsIds: string[]) => Promise<void>;
  divisionId: string;
  divisionCode?: string | null;
  divisionName?: string | null;
  allProjectDraws: ProjectDraw[];
  selectedProjectDraws: { [key: string]: string[] };
  onSelectionChange: (divisionId: string, newSelectedProjectDraws: string[]) => void;
  open: boolean;
  onClose: () => void;
  isReportDirty: boolean;
}

const ProjectDrawSelectionDialog: React.FC<ProjectDrawSelectionDropdownProps> = ({
  divisionId,
  divisionCode,
  divisionName,
  allProjectDraws,
  selectedProjectDraws,
  onSelectionChange,
  open,
  onClose,
  updateDivisionDraws,
  isReportDirty,
}) => {
  const queryClient = useQueryClient();
  const [isUpdating, setIsUpdating] = React.useState(false);

  const { getConfirmation } = usePrompt();

  const [localSelectedDraws, setLocalSelectedDraws] = React.useState(selectedProjectDraws[divisionId] || []);

  // Organize draws by projects
  // Organize and sort draws by projects
  const projectsWithDraws = useMemo(() => {
    const projectMapping: ProjectsWithDraws = {};
    allProjectDraws
      .filter(d => d.isEnabled !== false)
      .forEach(draw => {
        const projectId = draw.project?.projectId;
        if (projectId) {
          if (!projectMapping[projectId] && draw.project) {
            projectMapping[projectId] = {
              projectName: draw.project?.projectTitle,
              draws: [],
            };
          }
          projectMapping[projectId].draws.push(draw);
        }
      });

    // Sort draws within each project
    Object.values(projectMapping).forEach(project => {
      project.draws.sort((a: any, b: any) => a.name.localeCompare(b.name));
    });

    // Sort projects by name
    return Object.keys(projectMapping)
      .sort((a, b) => projectMapping[a].projectName.localeCompare(projectMapping[b].projectName))
      .reduce((sortedProjectsWithDraws: ProjectsWithDraws, projectId) => {
        sortedProjectsWithDraws[projectId] = projectMapping[projectId];
        return sortedProjectsWithDraws;
      }, {});
  }, [allProjectDraws]);

  const isProjectSelected = (projectId: any) => {
    return projectsWithDraws[projectId]?.draws.some((draw: { id: string }) => localSelectedDraws.includes(draw.id));
  };

  const handleProjectCheckbox = (projectId: any) => {
    const projectDraws = projectsWithDraws[projectId].draws;
    let updatedSelectedDraws;

    if (isProjectSelected(projectId)) {
      updatedSelectedDraws = localSelectedDraws.filter(id => !projectDraws.some((draw: { id: string }) => draw.id === id));
    } else {
      const newSelectedDrawsSet = new Set([...localSelectedDraws, ...projectDraws.map((draw: { id: any }) => draw.id)]);
      updatedSelectedDraws = Array.from(newSelectedDrawsSet);
    }

    setLocalSelectedDraws(updatedSelectedDraws);
    // onSelectionChange(divisionId, updatedSelectedDraws);
  };

  const handleDrawCheckbox = (projectId: any, drawId: any) => {
    const updatedSelectedDraws = localSelectedDraws.includes(drawId)
      ? localSelectedDraws.filter(id => id !== drawId)
      : [...localSelectedDraws, drawId];

    setLocalSelectedDraws(updatedSelectedDraws);
    // onSelectionChange(divisionId, updatedSelectedDraws);
  };

  const handleUpdateClick = async () => {
    try {
      setIsUpdating(true);
      if (isReportDirty) {
        const isConfirmed = await getConfirmation({
          description: 'Updating Selected Projects/Draws will overwrite any unsaved changes. Are you sure you want to continue?',
          confirmButton: {
            label: 'Continue',
            color: 'error',
          },
          cancelButton: {
            label: 'Cancel',
            color: 'secondary',
          },
        });
        if (!isConfirmed) return;
      }
      // API call to update the division projects
      await updateDivisionDraws(divisionId, localSelectedDraws);
      onSelectionChange(divisionId, localSelectedDraws);
      toast.success(`Division '${divisionCode}-${divisionName}' updated successfully!`);

      onClose();
    } catch (error) {
      handleError(error);
    } finally {
      setIsUpdating(false);
    }
  };

  const handleDialogClose = () => {
    // Reset to the original state
    onClose();
  };

  const dialogTitle = divisionCode && divisionName ? `${divisionCode} - ${divisionName}` : divisionName || 'Select Projects';
  return (
    <Dialog onClose={onClose} open={open} sx={{ minWidth: 900 }} fullWidth>
      <DialogTitle>{dialogTitle}</DialogTitle>
      <DialogContent dividers sx={{ overflowY: 'auto', maxHeight: '400px' }}>
        <List>
          {Object.keys(projectsWithDraws).map(projectId => (
            <React.Fragment key={projectId}>
              <ListItemButton onClick={() => handleProjectCheckbox(projectId)}>
                <Checkbox checked={isProjectSelected(projectId)} />
                <ListItemText primary={<Typography variant='body1'>{projectsWithDraws[projectId].projectName}</Typography>} />
              </ListItemButton>
              {projectsWithDraws[projectId].draws.map(draw => (
                <ListItemButton
                  key={draw.id}
                  sx={{ paddingLeft: '30px' }}
                  onClick={() => (draw.id ? handleDrawCheckbox(projectId, draw.id) : undefined)}
                >
                  <Checkbox checked={draw.id ? localSelectedDraws.includes(draw.id) : false} />
                  <ListItemText primary={<Typography variant='body2'>{draw.name}</Typography>} />
                </ListItemButton>
              ))}
            </React.Fragment>
          ))}
        </List>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleDialogClose} variant='outlined' color='error' disabled={isUpdating}>
          Cancel
        </Button>
        <Button onClick={handleUpdateClick} variant='contained' loading={isUpdating} disabled={isUpdating}>
          Update
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default React.memo(ProjectDrawSelectionDialog);
